import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Users, BarChart3, TrendingUp, AlertTriangle, 
  Filter, Search, RefreshCw, Plus, Settings, ChevronDown,
  Factory, Shield, Activity, Clock, CheckCircle2, XCircle,
  Target, Zap, Globe, PieChart, LineChart, Loader2,
  ArrowUpRight, ArrowDownRight, Eye, Calendar,
  Sparkles, Layers, Gauge, Award, Star, Flame,
  Cpu, Database, Network, Wifi, Signal
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Cell,
  Pie,
  BarChart as RechartsBarChart,
  Bar,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar,
  Legend
} from 'recharts';
import { getSuperAdminDashboard, getPlantData } from '../../services/operations/superAdminAPI';
import PlantManagement from './PlantManagement';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { cn, formatNumber, getStatusColor, getRiskColor } from '../../utils/utils';
import toast from 'react-hot-toast';

const SuperAdminDashboard = () => {
  const { token } = useSelector(state => state.auth);
  const { user } = useSelector(state => state.profile);

  // State Management
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState('all');
  const [plantSpecificData, setPlantSpecificData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPlantManagement, setShowPlantManagement] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeView, setActiveView] = useState('overview');

  // Fetch Dashboard Data
  const fetchDashboardData = async () => {
    setIsRefreshing(true);
    try {
      const response = await getSuperAdminDashboard(token);
      if (response?.success) {
        setDashboardData(response.data);
      } else {
        toast.error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Error loading dashboard");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch Plant Specific Data
  const fetchPlantData = async (plantId) => {
    if (plantId === 'all') {
      setPlantSpecificData(null);
      return;
    }
    
    try {
      const response = await getPlantData(plantId, token);
      if (response?.success) {
        setPlantSpecificData(response.data);
      }
    } catch (error) {
      console.error("Error fetching plant data:", error);
      toast.error("Error loading plant data");
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (selectedPlant !== 'all') {
      fetchPlantData(selectedPlant);
    } else {
      setPlantSpecificData(null);
    }
  }, [selectedPlant]);

  // Filter and search plants
  const filteredPlants = useMemo(() => {
    if (!dashboardData?.plants) return [];
    
    let plants = [...dashboardData.plants];
    
    if (searchTerm) {
      plants = plants.filter(plant => 
        plant.plantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.plantCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      plants = plants.filter(plant => plant.status.toLowerCase() === filterStatus);
    }
    
    return plants;
  }, [dashboardData?.plants, searchTerm, filterStatus]);

  // Chart colors
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br  flex items-center justify-center relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center relative z-10"
        >
          <div className="relative mb-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-20 h-20 mx-auto"
            >
              <div className="w-full h-full border-4 border-blue-500/30 border-t-blue-500 rounded-full"></div>
            </motion.div>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 w-20 h-20 mx-auto bg-blue-500/20 rounded-full blur-xl"
            ></motion.div>
          </div>
          
          <motion.h2 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4"
          >
            Loading Super Admin Dashboard
          </motion.h2>
          
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-black/60 text-lg"
          >
            Preparing your analytics and insights...
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center space-x-2 mt-6"
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                className="w-2 h-2 bg-blue-400 rounded-full"
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (showPlantManagement) {
    return (
      <PlantManagement 
        onBack={() => setShowPlantManagement(false)}
        onRefresh={fetchDashboardData}
      />
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"
        />
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '2s' }}
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"
        />
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '4s' }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-500/5 to-cyan-500/5 rounded-full blur-3xl"
        />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <motion.div 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="relative z-5"
      >
        <div className="">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="flex items-center gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  className="p-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl shadow-2xl"
                >
                  <Sparkles className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h1 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-2xl font-bold text-black"
                  >
                    Super Admin Dashboard
                  </motion.h1>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-black/70 text-lg mt-2 font-medium"
                  >
                    Welcome back, <span className="text-blue-300">{user?.firstName}</span>! 
                    Managing <span className="text-black font-semibold">{dashboardData?.overview?.totalPlants || 0}</span> plants 
                    across your organization.
                  </motion.p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                {/* Plant Filter */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <select
                    value={selectedPlant}
                    onChange={(e) => setSelectedPlant(e.target.value)}
                    className="bg-white/10 backdrop-blur-xl text-black border border-white/20 rounded-2xl px-4 py-2 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <option value="all" className="text-black bg-white">All Plants</option>
                    {dashboardData?.plants?.map(plant => (
                      <option key={plant.plantId} value={plant.plantId} className="text-black bg-white">
                        {plant.plantName} ({plant.plantCode})
                      </option>
                    ))}
                  </select>
                 
                </motion.div>

                <Button
                  onClick={fetchDashboardData}
                  disabled={isRefreshing}
                  variant="secondary"
                  className="gap-2 shadow-lg hover:shadow-xl"
                >
                  <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                
                </Button>

                <Button
                  onClick={() => setShowPlantManagement(true)}
                  className="gap-2 shadow-lg hover:shadow-xl text-white"
                >
                  <Settings className="h-5 w-4" />
                  Manage Plants
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Key Metrics */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {[
            {
              title: "Total Plants",
              value: dashboardData?.overview?.totalPlants || 0,
              change: "+2.5%",
              trend: "up",
              icon: Building2,
              gradient: "from-blue-500 via-blue-600 to-cyan-500",
              bgGradient: "from-blue-500/5 to-cyan-500/5"
            },
            {
              title: "Total Users",
              value: (dashboardData?.overview?.totalAdmins || 0) + (dashboardData?.overview?.totalEmployees || 0),
              change: "+8.2%",
              trend: "up",
              icon: Users,
              gradient: "from-emerald-500 via-emerald-600 to-teal-500",
              bgGradient: "from-emerald-500/5 to-teal-500/5",
              subtitle: `${dashboardData?.overview?.totalAdmins || 0} Admins, ${dashboardData?.overview?.totalEmployees || 0} Employees`
            },
            {
              title: "Total Drawings",
              value: dashboardData?.overview?.totalDrawings || 0,
              change: "+12.3%",
              trend: "up",
              icon: BarChart3,
              gradient: "from-purple-500 via-purple-600 to-pink-500",
              bgGradient: "from-purple-500/5 to-pink-500/5",
              subtitle: `${dashboardData?.overview?.totalNodes || 0} Nodes`
            },
            {
              title: "High Risk Items",
              value: dashboardData?.overview?.highRiskItems || 0,
              change: "-5.1%",
              trend: "down",
              icon: AlertTriangle,
              gradient: "from-red-500 via-red-600 to-orange-500",
              bgGradient: "from-red-500/5 to-orange-500/5"
            }
          ].map((metric, index) => (
            <motion.div key={index} variants={itemVariants}>
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="group cursor-pointer"
              >
                <Card className={`relative overflow-hidden bg-gradient-to-br ${metric.bgGradient} border-white/20 hover:border-white/40 transition-all duration-300`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                  <CardContent className="p-6 relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <p className="text-black/80 text-sm font-medium mb-2 tracking-wide uppercase">{metric.title}</p>
                        <motion.p 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: index * 0.1 }}
                          className="text-4xl font-bold text-black mb-2"
                        >
                          {formatNumber(metric.value)}
                        </motion.p>
                        {metric.subtitle && (
                          <p className="text-black/60 text-xs font-medium">{metric.subtitle}</p>
                        )}
                      </div>
                      <motion.div 
                        whileHover={{ rotate: 10, scale: 1.1 }}
                        className={`p-4 bg-gradient-to-r ${metric.gradient} rounded-2xl shadow-2xl group-hover:shadow-3xl transition-all duration-300`}
                      >
                        <metric.icon className="h-8 w-8 text-black" />
                      </motion.div>
                    </div>
                    {/* <div className="flex items-center text-sm">
                      <motion.div
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.1 + 0.2 }}
                        className="flex items-center"
                      >
                        {metric.trend === 'up' ? (
                          <ArrowUpRight className="h-4 w-4 text-emerald-400 mr-1" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-400 mr-1" />
                        )}
                        <span className={`font-semibold ${metric.trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {metric.change}
                        </span>
                        <span className="text-black/60 ml-2">vs last month</span>
                      </motion.div>
                    </div> */}
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Charts Section */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8"
        >
          {/* Performance Trends */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <motion.div whileHover={{ scale: 1.02 }} className="h-full">
              <Card className="h-full bg-gradient-to-br from-blue-500/10 to-white/5 border-white/20 hover:border-white/30 transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                        <LineChart className="h-6 w-6 text-black" />
                      </div>
                      <span className="bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                        Performance Trends
                      </span>
                    </CardTitle>
                    <Badge variant="secondary" className="backdrop-blur-sm">Last 6 Months</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dashboardData?.monthlyTrends || []}>
                        <defs>
                          <linearGradient id="colorDrawings" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorCompletions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="month" 
                          stroke="rgba(0, 0, 0, 0.7)" 
                          fontSize={12}
                          fontWeight={500}
                        />
                        <YAxis 
                          stroke="rgba(0, 0, 0, 0.7)" 
                          fontSize={12}
                          fontWeight={500}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '16px',
                            backdropFilter: 'blur(20px)',
                            boxShadow: '0 25px 50px -12px rgba(255, 255, 255, 0.25)'
                          }} 
                        />
                        <Area
                          type="monotone"
                          dataKey="drawings"
                          stroke="#3B82F6"
                          fillOpacity={1}
                          fill="url(#colorDrawings)"
                          strokeWidth={3}
                        />
                        <Area
                          type="monotone"
                          dataKey="completions"
                          stroke="#10B981"
                          fillOpacity={1}
                          fill="url(#colorCompletions)"
                          strokeWidth={3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Risk Distribution */}
          <motion.div variants={itemVariants}>
            <motion.div whileHover={{ scale: 1.02 }} className="h-full">
              <Card className="h-full bg-gradient-to-br from-white/10 to-white/5 border-white/20 hover:border-white/30 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                      <PieChart className="h-6 w-6 text-black" />
                    </div>
                    <span className="bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                      Risk Distribution
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'Low Risk', value: dashboardData?.riskDistribution?.low || 0, color: '#10B981' },
                            { name: 'Medium Risk', value: dashboardData?.riskDistribution?.medium || 0, color: '#F59E0B' },
                            { name: 'High Risk', value: dashboardData?.riskDistribution?.high || 0, color: '#EF4444' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {[
                            { name: 'Low Risk', value: dashboardData?.riskDistribution?.low || 0, color: '#10B981' },
                            { name: 'Medium Risk', value: dashboardData?.riskDistribution?.medium || 0, color: '#F59E0B' },
                            { name: 'High Risk', value: dashboardData?.riskDistribution?.high || 0, color: '#EF4444' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '16px',
                            backdropFilter: 'blur(20px)',
                            boxShadow: '0 25px 50px -12px rgba(255, 255, 255, 0.25)'
                          }} 
                        />
                        <Legend 
                          wrapperStyle={{ color: 'white', fontSize: '14px', fontWeight: '500' }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Plant Performance and Recent Activity */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8"
        >
          {/* Top Plant Performance */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card className="bg-gradient-to-br from-white/10 to-white/5 border-white/20 hover:border-white/30 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl">
                      <Award className="h-6 w-6 text-black" />
                    </div>
                    <span className="bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                      Top Plant Performance
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={dashboardData?.plants?.slice(0, 5) || []}>
                        <defs>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.6}/>
                          </linearGradient>
                          <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0.6}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="plantCode" 
                          stroke="rgba(0, 0, 0, 0.7)" 
                          fontSize={12}
                          fontWeight={500}
                        />
                        <YAxis 
                          stroke="rgba(0, 0, 0, 0.7)" 
                          fontSize={12}
                          fontWeight={500}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.9)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '16px',
                            backdropFilter: 'blur(20px)',
                            boxShadow: '0 25px 50px -12px rgba(255, 255, 255, 0.25)'
                          }} 
                        />
                        <Bar 
                          dataKey="totalDrawings" 
                          fill="url(#colorTotal)" 
                          radius={[8, 8, 0, 0]} 
                          name="Total Drawings"
                        />
                        <Bar 
                          dataKey="completedDrawings" 
                          fill="url(#colorCompleted)" 
                          radius={[8, 8, 0, 0]} 
                          name="Completed Drawings"
                        />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div variants={itemVariants}>
            <motion.div whileHover={{ scale: 1.02 }} className="h-full">
              <Card className="h-full bg-gradient-to-br from-white/10 to-white/5 border-white/20 hover:border-white/30 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl">
                      <Activity className="h-6 w-6 text-black" />
                    </div>
                    <span className="bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                      Recent Activity
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-80 overflow-y-auto custom-scrollbar">
                    <AnimatePresence>
                      {dashboardData?.recentActivity?.slice(0, 8).map((activity, index) => (
                        <motion.div 
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, x: 5 }}
                          className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20"
                        >
                          <motion.div 
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            className={`p-3 rounded-xl ${
                              activity.status === 'Completed' 
                                ? 'bg-gradient-to-r from-emerald-500 to-green-500' 
                                : 'bg-gradient-to-r from-amber-500 to-orange-500'
                            } shadow-lg`}
                          >
                            {activity.status === 'Completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-black" />
                            ) : (
                              <Clock className="h-5 w-5 text-black" />
                            )}
                          </motion.div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-black truncate mb-1">
                              Drawing {activity.drawingNumber}
                            </p>
                            <p className="text-xs text-black/60 mb-2">
                              {activity.plantName} • {new Date(activity.createdAt).toLocaleDateString()}
                            </p>
                            <Badge 
                              variant={activity.status === 'Completed' ? 'success' : 'warning'}
                              className="text-xs font-medium"
                            >
                              {activity.status}
                            </Badge>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Plants Overview */}
        <motion.div 
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <Card className="bg-gradient-to-br from-white/10 to-white/5 border-white/20 hover:border-white/30 transition-all duration-300">
            <CardHeader>
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl">
                    <Factory className="h-6 w-6 text-black" />
                  </div>
                  <span className="bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                    Plants Overview
                  </span>
                </CardTitle>
                
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search plants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 pr-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-black/60 text-sm w-64 transition-all duration-300"
                    />
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black/60 h-5 w-5" />
                  </div>
                  
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-white/10 backdrop-blur-xl border border-black/20 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black transition-all duration-300"
                  >
                    <option value="all" className="text-slate-800 bg-white rounded-xl ">All Status</option>
                    <option value="active" className="text-slate-800 bg-white rounded-xl">Active</option>
                    <option value="inactive" className="text-slate-800 bg-white rounded-xl">Inactive</option>
                    <option value="maintenance" className="text-slate-800 bg-white rounded-xl">Maintenance</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Plant</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Admin</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Employees</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Drawings</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Safety Rating</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-black/80 uppercase tracking-wider">Risk Level</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    <AnimatePresence>
                      {filteredPlants.map((plant, index) => (
                        <motion.tr 
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                          className="hover:bg-white/5 transition-all duration-300"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-4">
                              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                                <Factory className="h-6 w-6 text-black" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-black">{plant.plantName}</div>
                                <div className="text-sm text-black/60">{plant.plantCode} • {plant.location}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-black font-medium">{plant.admin}</div>
                            <div className="text-sm text-black/60">{plant.adminEmail || 'Not assigned'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              variant={
                                plant.status === 'Active' ? 'success' :
                                plant.status === 'Inactive' ? 'destructive' : 'warning'
                              }
                              className="font-medium"
                            >
                              {plant.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-black font-medium">
                            {plant.totalEmployees}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-black font-medium">
                              {plant.totalDrawings} total
                            </div>
                            <div className="text-xs text-black/60">
                              {plant.completedDrawings} completed, {plant.pendingDrawings} pending
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < plant.safetyRating ? 'text-amber-400 fill-current' : 'text-black/20'
                                  }`}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-white/10 rounded-full h-2 overflow-hidden">
                                <motion.div 
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((plant.averageRisk / 25) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.1 }}
                                  className={`h-full rounded-full ${
                                    plant.averageRisk < 6 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 
                                    plant.averageRisk < 15 ? 'bg-gradient-to-r from-amber-400 to-amber-500' : 
                                    'bg-gradient-to-r from-red-400 to-red-500'
                                  }`}
                                />
                              </div>
                              <span className="text-sm text-black font-medium min-w-[2rem]">
                                {plant.averageRisk}
                              </span>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default SuperAdminDashboard;